import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import React, { useCallback, useMemo } from 'react'
import log from 'loglevel'
import { Chat } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { ReportsServicePromiseClient } from '@trustero/trustero-api-web/lib/reports/reports_grpc_web_pb'
import { useGrpcRevalidateByMethod } from 'src/components'
import { useUser } from 'src/components/async/Users/useUser'
import { TooltipIconButton } from 'src/components/Reusable/Buttons/TooltipIconButton'
import { CheckSVG, PrivateSVG, PublicSVG } from 'src/components/Icons/Basic'
import { useUpdateChat } from '../trusteroIntelligence.hooks'

export type ChatVisibilityProps = {
  chat?: Chat
  isPrivate: boolean
  setIsPrivate: (isPrivate: boolean) => void
}

export const ChatVisibility = ({
  chat,
  isPrivate,
  setIsPrivate,
}: ChatVisibilityProps): JSX.Element => {
  const updateChat = useUpdateChat()
  const revalidate = useGrpcRevalidateByMethod()
  const user = useUser()
  const owner = chat?.getOwner()

  const isOwner = useMemo(
    () => owner && owner.getId() === user?.data?.getId(),
    [owner, user],
  )

  const saveChat = useCallback(
    async (chat: Chat) => {
      try {
        await updateChat(chat)
        // An open report's Publish depends on the chat being public.
        await revalidate(
          ReportsServicePromiseClient.prototype.listReportVersions,
        )
      } catch (error) {
        log.error('Error updating chat', error)
      }
    },
    [updateChat, revalidate],
  )

  const onClick = useCallback(async () => {
    if (chat) {
      try {
        chat.setIsPrivate(!isPrivate)
        await saveChat(chat)
      } catch (error) {
        log.error('Error updating chat', error)
      }
    }
    setIsPrivate(!isPrivate)
  }, [chat, saveChat, isPrivate, setIsPrivate])

  const tooltipText = useMemo(() => {
    const messages = [`This is a ${isPrivate ? 'private' : 'public'} chat.`]
    if (isOwner || !chat) {
      messages.push(`Click to make it ${isPrivate ? 'public' : 'private'}.`)
    }
    return messages.join(' ')
  }, [isPrivate, isOwner, chat])

  // Visibility saves through UpdateChat, so owning the chat is necessary but not
  // sufficient - the capability decides whether this user may change a chat at all.
  const canUpdateChat = useCanPerformAction(CAPABILITY.CAN_UPDATE_CHATS)
  const isDisabled = useMemo(() => {
    return !canUpdateChat || (chat && !isOwner)
  }, [canUpdateChat, chat, isOwner])

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <TooltipIconButton
        disabled={isDisabled}
        onClick={onClick}
        icon={isPrivate ? <PrivateSVG /> : <PublicSVG />}
        successIcon={<CheckSVG />}
        tooltipText={tooltipText}
        tooltipClickedText={`Chat is now ${isPrivate ? 'private' : 'public'}.`}
        placement="left"
      />
    </div>
  )
}
